import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  RiGridLine, RiFlashlightLine, RiFileTextLine, RiQuillPenLine,
  RiCheckLine, RiCloseLine, RiRefreshLine, RiArrowRightLine,
  RiTrophyLine, RiFullscreenLine, RiFilterLine, RiBrushLine,
} from 'react-icons/ri';
import { C } from '../../data/constants';
import { Tag, ProgressBar, ModalShell } from '../../components/shared/UI';
import { HIRAGANA, KATAKANA, GROUPS, GROUP_LABELS } from '../../data/kanaData';
import { getKana, getKanaProgress, getKanaSession, updateKanaProgress } from '../../api/content.api';
import { completeRoadmapStage } from '../../api/quiz.api';
import { NODE_BY_ID, stageHref } from '../../data/curriculum';
import StreakCelebration from '../../components/app/StreakCelebration';
import useStreakCelebration from '../../components/app/useStreakCelebration';
import useStudyGuard from '../../components/app/useStudyGuard';
import QuitConfirm from '../../components/shared/QuitConfirm';
import { playMark } from '../../components/brand/celebrate';
import SessionComplete from '../../components/app/SessionComplete';
/* kana-draw 1/6 — self-contained handwriting mode. See the removal note
   at the top of DrawMode.jsx; nothing outside this file references it. */
import DrawMode from '../../features/kana-draw/DrawMode';

/* ── helpers ─────────────────────────────────────────────────── */
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function getOptions(correct, all) {
  const others = shuffle(all.filter(k => k.romaji !== correct.romaji)).slice(0, 3);
  return shuffle([correct, ...others]);
}

const EMPTY_KANA_PROGRESS = { hiragana: {}, katakana: {} };

function clampMastery(value) {
  return Math.max(0, Math.min(100, value));
}

function masteryFor(progress, script, kana) {
  const value = Number(progress?.[script]?.[kana]);
  return Number.isFinite(value) ? clampMastery(value) : 0;
}

function fallbackSession(items) {
  const count = Math.min(items.length, 14 + Math.floor(Math.random() * 12));
  return shuffle(items).slice(0, count);
}

/* ── mode tab button ─────────────────────────────────────────── */
function ModeTab({ icon: Icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className="study-mode-tab"
      style={{
        display: 'flex', alignItems: 'center', gap: 7,
        padding: '9px 18px', borderRadius: 9, border: 'none', cursor: 'pointer',
        fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: 14,
        background: active ? C.accent : 'transparent',
        color: active ? '#fff' : C.muted,
        transition: 'all 0.2s',
      }}
    >
      <Icon size={15} />{label}
    </button>
  );
}

/* ══════════════════════════════════════════════════════════════
   CHART MODE
══════════════════════════════════════════════════════════════ */
function ChartMode({ data, type, script, progress, filterGroup, onFilterGroupChange }) {
  const [selected, setSelected] = useState(null);

  const sortedData = useMemo(() => [...data].sort((a, b) => (a.order || 0) - (b.order || 0)), [data]);
  const filtered = filterGroup === 'all' ? sortedData : sortedData.filter(k => k.group === filterGroup);
  const mainGroups = new Set(['vowels', 'k', 's', 't', 'n', 'h', 'm', 'y', 'r', 'w', 'special']);
  const mainItems = sortedData.filter(item => mainGroups.has(item.group));
  const dakutenItems = sortedData.filter(item => item.group === 'dakuten');
  const handakutenItems = sortedData.filter(item => item.group === 'handakuten');

  useEffect(() => {
    setSelected(null);
  }, [filterGroup]);

  const renderGrid = items => (
    <div className="kana-chart-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))', gap: 8 }}>
      {items.map(k => {
        const active = selected?.kana === k.kana;
        return (
          <button
            key={`${k.kana}-${k.order}`}
            type="button"
            className="kana-char-card"
            onClick={() => setSelected(active ? null : k)}
            style={{
              background: active ? C.accentDim : C.card,
              border: `1px solid ${active ? C.accentBorder : C.border}`,
              borderRadius: 12,
              padding: '14px 8px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.16s',
            }}
          >
            <div style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: 28, fontWeight: 900, lineHeight: 1, color: C.text }}>{k.kana}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 5, fontWeight: 600 }}>{k.romaji}</div>
            <div style={{ marginTop: 8, height: 4, borderRadius: 999, background: C.surface, overflow: 'hidden' }}>
              <div style={{ width: `${masteryFor(progress, script, k.kana)}%`, height: '100%', borderRadius: 999, background: C.progress }} />
            </div>
            <div style={{ fontSize: 10, color: C.faint, marginTop: 4, fontWeight: 800 }}>{masteryFor(progress, script, k.kana)}%</div>
          </button>
        );
      })}
    </div>
  );

  // One presentation on every screen size: an overlay. (Previously desktop got
  // an inline side panel and only mobile got the overlay, which meant the same
  // content was written out twice and could drift apart.)
  const detailModal = selected && (
    <ModalShell
      onClose={() => setSelected(null)}
      overlayClassName="kana-modal-overlay"
      boxClassName="kana-modal-box"
      label={`${selected.kana} details`}
    >
      <div style={{ textAlign: 'center', marginBottom: 18 }}>
        <div style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: 92, fontWeight: 900, lineHeight: 1, color: C.text, marginBottom: 8 }}>
          {selected.kana}
        </div>
        <div style={{ fontSize: 26, fontWeight: 900, color: C.accent, marginBottom: 6 }}>{selected.romaji}</div>
        <div style={{ fontSize: 12, color: C.muted }}>{type} · {GROUP_LABELS[selected.group]}</div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: C.muted, fontWeight: 800, marginBottom: 7 }}>
          <span>Learning</span>
          <span>{masteryFor(progress, script, selected.kana)}%</span>
        </div>
        <ProgressBar value={masteryFor(progress, script, selected.kana)} />
      </div>
      <button className="btn-ghost" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setSelected(null)}>Close</button>
    </ModalShell>
  );

  return (
    <div>
      <div className="kana-filter-bar">
        <button type="button" className={`kana-filter-chip${filterGroup === 'all' ? ' active' : ''}`} onClick={() => onFilterGroupChange('all')}>
          All
        </button>
        {GROUPS.map(g => (
          <button
            type="button"
            key={g}
            className={`kana-filter-chip${filterGroup === g ? ' active' : ''}`}
            onClick={() => onFilterGroupChange(g)}
          >
            {GROUP_LABELS[g]}
          </button>
        ))}
      </div>

      {filterGroup === 'all' ? (
        <div className="kana-chart-stack">
          <section className="kana-chart-section">
            <div className="kana-section-title">
              <h3>Main kana</h3>
              <span>{mainItems.length} characters</span>
            </div>
            {renderGrid(mainItems)}
          </section>
          {dakutenItems.length > 0 && (
            <section className="kana-chart-section">
              <div className="kana-section-title">
                <h3>Dakuten</h3>
                <span>Voiced sounds</span>
              </div>
              {renderGrid(dakutenItems)}
            </section>
          )}
          {handakutenItems.length > 0 && (
            <section className="kana-chart-section">
              <div className="kana-section-title">
                <h3>Handakuten</h3>
                <span>Semi-voiced sounds</span>
              </div>
              {renderGrid(handakutenItems)}
            </section>
          )}
        </div>
      ) : (
        <div className="kana-chart-layout">
          {renderGrid(filtered)}
        </div>
      )}

      {detailModal}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   FLASHCARD MODE
══════════════════════════════════════════════════════════════ */
function FlashcardMode({ script, progress, onGrade, flashcardState, setFlashcardState, fullscreen = false }) {
  const {
    cards = [],
    index = 0,
    flipped = false,
    known = [],
    unknown = [],
    done = false,
  } = flashcardState;

  if (!cards.length) {
    return (
      <div className="ks-card" style={{ padding: 32, textAlign: 'center', color: C.muted, fontWeight: 800 }}>
        Preparing your flashcards...
      </div>
    );
  }

  const card = cards[index];
  const total = cards.length;
  const pct = ((known.length + unknown.length) / total) * 100;

  const next = (result) => {
    onGrade(card, result === 'know');
    const isKnown = result === 'know';
    const isLast = index + 1 >= total;

    setFlashcardState(prev => ({
      ...prev,
      flipped: false,
      known: isKnown ? [...prev.known, index] : prev.known,
      unknown: isKnown ? prev.unknown : [...prev.unknown, index],
      done: isLast ? true : prev.done,
    }));

    if (!isLast) {
      setTimeout(() => {
        setFlashcardState(prev => ({ ...prev, index: prev.index + 1 }));
      }, 180);
    }
  };

  if (done) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
      <div className="anim-pop" style={{ textAlign: 'center', maxWidth: 360 }}>
        <RiTrophyLine size={48} style={{ color: C.yellow, marginBottom: 12 }} />
        <h2 style={{ fontSize: 24, fontWeight: 900, marginBottom: 8 }}>Session Complete!</h2>
        <p style={{ color: C.muted, marginBottom: 20, fontSize: 14 }}>
          <span style={{ color: C.green, fontWeight: 800 }}>{known.length} known</span> · <span style={{ color: C.red, fontWeight: 800 }}>{unknown.length} to review</span>
        </p>
        <button className="btn-primary" onClick={() => window.location.reload()}><RiRefreshLine /> Study Again</button>
      </div>
    </div>
  );

  return (
    <div className={`study-stage${fullscreen ? ' fullscreen' : ''}`} style={{ '--stage-w': fullscreen ? '520px' : '470px' }}>
      <div className="study-stage-body">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 13, color: C.muted }}>{index + 1} / {total}</span>
        <div style={{ display: 'flex', gap: 12, fontSize: 13 }}>
          <span style={{ color: C.muted, fontWeight: 800 }}>Mastery {masteryFor(progress, script, card.kana)}%</span>
          <span style={{ color: C.green, fontWeight: 700 }}>✓ {known.length}</span>
          <span style={{ color: C.red, fontWeight: 700 }}>✗ {unknown.length}</span>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}><ProgressBar value={pct} /></div>

      {/* Card */}
      <div
        onClick={() => {
          setFlashcardState(prev => ({ ...prev, flipped: !prev.flipped }));
        }}
        style={{
          background: C.card, border: `1px solid ${flipped ? C.accentBorder : C.border}`,
          borderRadius: 20, padding: fullscreen ? '44px 36px' : '40px 32px', textAlign: 'center',
          cursor: 'pointer', minHeight: fullscreen ? 260 : 220, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 12,
          transition: 'border-color 0.3s', marginBottom: 16,
        }}
      >
        <div style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: fullscreen ? 96 : 78, fontWeight: 900, lineHeight: 1 }}>
          {card.kana}
        </div>
        {/* One well for both faces, sized to the taller — revealing the
            answer must not change the card's height. */}
        <div style={{ minHeight: fullscreen ? 90 : 78, display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%' }}>
          {flipped ? (
            <div className="anim-pop">
              <div style={{ height: 1, background: C.border, margin: '0 0 12px' }} />
              <div style={{ fontSize: fullscreen ? 30 : 26, fontWeight: 900, color: C.accent }}>{card.romaji}</div>
              <div style={{ fontSize: fullscreen ? 14 : 12, color: C.muted, marginTop: 6 }}>{GROUP_LABELS[card.group]}</div>
            </div>
          ) : (
            <div style={{ fontSize: fullscreen ? 14 : 12, color: C.faint }}>Tap to reveal meaning</div>
          )}
        </div>
      </div>

      </div>

      <div className="study-footer">
        <div className="study-footer-inner">
          <button className="btn-ghost" disabled={!flipped} onClick={() => next('unknown')}
                  style={{ flex: 1, justifyContent: 'center', borderColor: `${C.red}38`, color: C.red, opacity: flipped ? 1 : 0.34 }}>
            <RiCloseLine size={16} /> Still learning
          </button>
          <button className="btn-primary" disabled={!flipped} onClick={() => next('know')}
                  style={{ flex: 1, justifyContent: 'center', background: C.green, marginLeft: 0 }}>
            <RiCheckLine size={16} /> Got it
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   QUIZ MODE  (multiple choice — kana → romaji AND romaji → kana)
══════════════════════════════════════════════════════════════ */
function QuizMode({ allData, script, progress, onGrade, quizState, setQuizState, fullscreen = false, keyboardEnabled = true }) {
  const {
    cards = [],
    options = [],
    index = 0,
    selected = null,
    score = 0,
    done = false,
    reverse = false,
  } = quizState;

  const hasCards = cards.length > 0;
  const safeIndex = hasCards ? Math.min(index, cards.length - 1) : 0;
  const card = hasCards ? cards[safeIndex] : null;
  const currentOptions = hasCards ? (options[safeIndex] || getOptions(card, allData)) : [];
  const isLast = hasCards && safeIndex + 1 >= cards.length;
  const pct = hasCards ? (safeIndex / cards.length) * 100 : 0;

  const choose = (opt) => {
    if (selected || !card) return;
    const correct = opt.romaji === card.romaji;
    setQuizState(prev => ({
      ...prev,
      selected: opt,
      score: correct ? prev.score + 1 : prev.score,
    }));
    onGrade(card, correct);
    playMark(correct ? 'bounce' : 'shake');
  };

  const next = useCallback(() => {
    if (!hasCards) return;
    if (isLast) {
      setQuizState(prev => ({ ...prev, selected: null, done: true }));
      return;
    }

    setQuizState(prev => ({ ...prev, selected: null, index: prev.index + 1 }));
  }, [hasCards, isLast, setQuizState]);

  useEffect(() => {
    if (done || !keyboardEnabled) return;

    const onKeyDown = (event) => {
      if (event.key !== 'Enter' || event.repeat) return;

      event.preventDefault();

      // Only move forward if an answer has been selected
      if (selected) {
        next();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [selected, next, done, keyboardEnabled]);

  if (!hasCards) {
    return (
      <div className="ks-card" style={{ padding: 32, textAlign: 'center', color: C.muted, fontWeight: 800 }}>
        Preparing your quiz...
      </div>
    );
  }

  if (done) {
    const pctScore = Math.round((score / cards.length) * 100);
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <div className="anim-pop" style={{ textAlign: 'center', maxWidth: 360 }}>
          <div style={{ fontSize: 60, fontWeight: 900, color: pctScore >= 70 ? C.green : C.red, letterSpacing: -2, marginBottom: 8 }}>{pctScore}%</div>
          <h2 style={{ fontSize: 22, fontWeight: 900, marginBottom: 8 }}>{pctScore >= 80 ? 'Excellent!' : pctScore >= 60 ? 'Good job!' : 'Keep practicing!'}</h2>
          <p style={{ color: C.muted, marginBottom: 20, fontSize: 14 }}>{score} / {cards.length} correct</p>
          <button className="btn-primary" onClick={() => window.location.reload()}><RiRefreshLine /> Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`study-stage${fullscreen ? ' fullscreen' : ''}`} style={{ '--stage-w': fullscreen ? '560px' : '520px' }}>
      <div className="study-stage-body">
      {/* Reverse toggle */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <span style={{ fontSize: 12, color: C.muted }}>
          {safeIndex + 1} / {cards.length} · Score: <span style={{ color: C.accent, fontWeight: 700 }}>{score}</span> · Mastery {masteryFor(progress, script, card.kana)}%
        </span>
        <button
          className="btn-ghost"
          style={{ padding: fullscreen ? '8px 16px' : '6px 14px', fontSize: fullscreen ? 13 : 12 }}
          onClick={() => setQuizState(prev => ({ ...prev, reverse: !prev.reverse }))}
        >
          {reverse ? 'Romaji → Kana' : 'Kana → Romaji'}
        </button>
      </div>

      <div style={{ marginBottom: 20 }}><ProgressBar value={pct} /></div>

      {/* Question */}
      <div className="ks-card" style={{ padding: fullscreen ? '28px 26px' : '24px', textAlign: 'center', marginBottom: 14, minHeight: fullscreen ? 190 : undefined, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: fullscreen ? 13 : 11, color: C.muted, fontWeight: 700, letterSpacing: 0.5, marginBottom: 12 }}>
          {reverse ? 'Which kana is this?' : 'What is the romaji for this?'}
        </div>
        <div
          style={{
            fontFamily: reverse
              ? "'Nunito', sans-serif"
              : "'Noto Sans JP', sans-serif",

            // Slight visual compensation
            fontSize: reverse
              ? (fullscreen ? 74 : 60)
              : (fullscreen ? 78 : 62),

            fontWeight: 900,
            lineHeight: 1,
            color: C.text,
          }}
        >
          {reverse ? card.romaji : card.kana}
        </div>
      </div>

      {/* Options */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: fullscreen ? 10 : 9 }}>
        {currentOptions.map((opt, i) => {
          const isCorrect = opt.romaji === card.romaji;
          const isSelected = selected?.romaji === opt.romaji;
          let bg = C.surface, bc = C.border, col = C.text;
          if (selected) {
            if (isCorrect) { bg = `${C.green}16`; bc = `${C.green}45`; col = C.green; }
            else if (isSelected) { bg = `${C.red}16`; bc = `${C.red}45`; col = C.red; }
          }
          return (
            <button
              key={i}
              className="choice-btn"
              disabled={!!selected}
              onClick={() => choose(opt)}
              style={{ background: bg, borderColor: bc, color: col, justifyContent: 'center', padding: fullscreen ? '13px 14px' : '12px', minHeight: fullscreen ? 52 : 48 }}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',

                  width: '100%',
                  height: '100%',

                  fontFamily: reverse
                    ? "'Noto Sans JP', sans-serif"
                    : "'Nunito', sans-serif",

                  fontSize: fullscreen ? 20 : 18,
                  fontWeight: 900,
                  lineHeight: 1,
                }}
              >
                {reverse ? opt.kana : opt.romaji}
              </span>
              {selected && isCorrect && <RiCheckLine size={14} style={{ color: C.green, marginLeft: 6 }} />}
              {selected && isSelected && !isCorrect && <RiCloseLine size={14} style={{ color: C.red, marginLeft: 6 }} />}
            </button>
          );
        })}
      </div>

      </div>

      <div className={`study-footer${selected ? (selected.romaji === card.romaji ? ' correct' : ' wrong') : ''}`}>
        <div className="study-footer-inner">
          {selected ? (
            <div className="study-feedback">
              <strong>
                {selected.romaji === card.romaji
                  ? <><RiCheckLine size={15} /> Correct</>
                  : <><RiCloseLine size={15} /> Not quite</>}
              </strong>
              <span>{card.kana} = {card.romaji}</span>
            </div>
          ) : (
            <span className="study-footer-hint">{reverse ? 'Pick the kana' : 'Pick the romaji'}</span>
          )}

          <button className="btn-primary" disabled={!selected} onClick={next}>
            {isLast ? 'See results' : 'Next'} <RiArrowRightLine />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   WRITING MODE  (type the romaji)
══════════════════════════════════════════════════════════════ */
function WritingMode({ script, progress, onGrade, writingState, setWritingState, fullscreen = false, keyboardEnabled = true }) {
  const {
    cards = [],
    index = 0,
    input = '',
    result = null,
    score = 0,
    done = false,
    showHint = false,
  } = writingState;
  const inputRef = useRef(null);

  const hasCards = cards.length > 0;
  const safeIndex = hasCards ? Math.min(index, cards.length - 1) : 0;
  const card = hasCards ? cards[safeIndex] : null;
  const pct = hasCards ? (safeIndex / cards.length) * 100 : 0;
  const isLast = hasCards && safeIndex + 1 >= cards.length;

  useEffect(() => {
    if (keyboardEnabled) inputRef.current?.focus();
  }, [index, keyboardEnabled]);

  const check = useCallback(() => {
    if (!input.trim() || !card) return;
    const correct = input.trim().toLowerCase() === card.romaji.toLowerCase();
    setWritingState(prev => ({
      ...prev,
      result: correct ? 'correct' : 'wrong',
      score: correct ? prev.score + 1 : prev.score,
    }));
    onGrade(card, correct);
    playMark(correct ? 'bounce' : 'shake');
  }, [card, input, onGrade, setWritingState]);

  const next = useCallback(() => {
    if (!hasCards) return;
    setWritingState(prev => ({
      ...prev,
      input: '',
      result: null,
      showHint: false,
      done: isLast ? true : prev.done,
      index: isLast ? prev.index : prev.index + 1,
    }));
  }, [hasCards, isLast, setWritingState]);

  useEffect(() => {
    if (done || !keyboardEnabled) return;

    const onKeyDown = (event) => {
      if (event.key !== 'Enter' || event.repeat) return;

      event.preventDefault();

      if (result) {
        next(); // second Enter -> next card
      } else if (input.trim()) {
        check(); // first Enter -> check answer
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [check, next, result, done, input, keyboardEnabled]);

  if (!hasCards) {
    return (
      <div className="ks-card" style={{ padding: 32, textAlign: 'center', color: C.muted, fontWeight: 800 }}>
        Preparing your writing session...
      </div>
    );
  }

  if (done) {
    const pctScore = Math.round((score / cards.length) * 100);
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <div className="anim-pop" style={{ textAlign: 'center', maxWidth: 360 }}>
          <div style={{ fontSize: 60, fontWeight: 900, color: pctScore >= 70 ? C.green : C.red, letterSpacing: -2, marginBottom: 8 }}>{pctScore}%</div>
          <h2 style={{ fontSize: 22, fontWeight: 900, marginBottom: 8 }}>{pctScore >= 80 ? 'Excellent!' : pctScore >= 60 ? 'Good job!' : 'Keep practicing!'}</h2>
          <p style={{ color: C.muted, marginBottom: 20, fontSize: 14 }}>{score} / {cards.length} correct</p>
          <button className="btn-primary" onClick={() => window.location.reload()}><RiRefreshLine /> Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`study-stage${fullscreen ? ' fullscreen' : ''}`}
      style={{ '--stage-w': fullscreen ? '500px' : '460px' }}
    >
      <div className="study-stage-body">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 12, color: C.muted }}>{safeIndex + 1} / {cards.length}</span>
        <span style={{ fontSize: 13, color: C.accent, fontWeight: 700 }}>Score: {score} · Mastery {masteryFor(progress, script, card.kana)}%</span>
      </div>

      <div style={{ marginBottom: 20 }}><ProgressBar value={pct} /></div>

      {/* Character display */}
      <div
        className="ks-card"
        style={{ padding: fullscreen ? '40px 32px' : '36px 28px', textAlign: 'center', marginBottom: 14, minHeight: fullscreen ? 210 : undefined, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
      >
        <div style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: fullscreen ? 100 : 82, fontWeight: 900, lineHeight: 1 }}>
          {card.kana}
        </div>
        <div style={{ fontSize: fullscreen ? 14 : 12, color: C.faint, marginTop: 12 }}>Study the shape, then type the romaji</div>
      </div>

      {/* Input */}
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 12, color: C.muted, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
          Type the romaji
        </label>
        <input
          ref={inputRef}
          className="ks-input"
          placeholder="e.g. ka, shi, tsu…"
          value={input}
          onChange={e => {
            setWritingState(prev => ({ ...prev, input: e.target.value, result: null }));
          }}
          disabled={!!result}
          style={{
            fontSize: fullscreen ? 18 : 17,
            textAlign: 'center',
            fontWeight: 700,
            padding: fullscreen ? 18 : undefined,
            borderColor:
              result === 'correct'
                ? C.green
                : result === 'wrong'
                  ? C.red
                  : undefined,
          }}
        />
      </div>

      {/* Hint — its space is reserved after checking, otherwise removing
          the row collapsed ~30px and slid the centred stage upward. */}
      <div style={{ textAlign: 'center', marginBottom: 12, minHeight: 18, visibility: result ? 'hidden' : 'visible' }}>
        <span
          style={{ fontSize: 12, color: C.muted, cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => setWritingState(prev => ({ ...prev, showHint: !prev.showHint }))}
        >
          {showHint ? `Answer: ${card.romaji}` : 'Show hint'}
        </span>
      </div>
      </div>

      <div className={`study-footer${result ? ` ${result}` : ''}`}>
        <div className="study-footer-inner">
          {result ? (
            <div className="study-feedback">
              <strong>
                {result === 'correct' ? <><RiCheckLine size={15} /> Correct</> : <><RiCloseLine size={15} /> Not quite</>}
              </strong>
              <span>{card.kana} = {card.romaji}</span>
            </div>
          ) : (
            <span className="study-footer-hint">Type the romaji, then check</span>
          )}

          {result ? (
            <button className="btn-primary" onClick={next}>
              {isLast ? 'See results' : 'Next'} <RiArrowRightLine />
            </button>
          ) : (
            <button className="btn-primary" onClick={check} disabled={!input.trim()}>
              Check <RiCheckLine />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   MAIN KANA PAGE
══════════════════════════════════════════════════════════════ */
const MODES = [
  { key: 'chart', label: 'Chart', icon: RiGridLine },
  { key: 'flashcard', label: 'Flashcards', icon: RiFlashlightLine },
  { key: 'quiz', label: 'Quiz', icon: RiFileTextLine },
  { key: 'writing', label: 'Writing', icon: RiQuillPenLine },
  { key: 'draw', label: 'Draw', icon: RiBrushLine },   /* kana-draw 2/6 */
];

const EMPTY_QUIZ_STATE = {
  cards: [],
  options: [],
  index: 0,
  selected: null,
  score: 0,
  done: false,
  reverse: false,
};

const EMPTY_FLASHCARD_STATE = {
  cards: [],
  index: 0,
  flipped: false,
  known: [],
  unknown: [],
  done: false,
};

/* kana-draw 3/6 — lifted for the same reason every other mode's state is:
   in fullscreen the page renders the mode twice (once inline, once in the
   portal), so state held inside the component would fork. */
const EMPTY_DRAW_STATE = {
  cards: [],
  index: 0,
  revealed: false,
  correct: 0,
  done: false,
};

/* A late review row (handakuten) can otherwise pull in every earlier row at
   once. Keep the whole row you are on and sample the history around it. */
const REVIEW_SESSION_MAX = 20;

const EMPTY_WRITING_STATE = {
  cards: [],
  index: 0,
  input: '',
  result: null,
  score: 0,
  done: false,
  showHint: false,
};

export default function Kana() {
  const navigate = useNavigate();

  /* The learning path deep-links one *stage* of one row, e.g.
       /kana?script=hiragana&groups=k,vowels&mode=quiz&lesson=hira-k&stage=3&focus=1
     `focus` means the exercise was launched from the path: it opens full
     screen and closing it goes back to the path, because that is where the
     learner came from and where their progress is shown.
     Everything is validated rather than trusted, so a hand-edited URL can
     only ever produce a harmless session. */
  const initial = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const s = params.get('script');
    const g = params.get('group');
    const m = params.get('mode');
    const modes = MODES.map(x => x.key);

    const pool = (params.get('groups') || '')
      .split(',')
      .map(x => x.trim())
      .filter(x => GROUPS.includes(x));

    const stageRaw = Number(params.get('stage'));
    const lesson = params.get('lesson');

    return {
      script: s === 'katakana' || s === 'hiragana' ? s : 'hiragana',
      group: g && GROUPS.includes(g) ? g : 'all',
      mode: m && modes.includes(m) ? m : 'chart',
      pool,
      focus: params.get('focus') === '1' && pool.length > 0 && !!m && modes.includes(m) && m !== 'chart',
      review: params.get('review') === '1',
      lesson: lesson && NODE_BY_ID[lesson] ? lesson : null,
      stage: Number.isInteger(stageRaw) && stageRaw >= 0 ? stageRaw : null,
    };
  }, []);

  const [script, setScript] = useState(initial.script); // 'hiragana' | 'katakana'
  const [mode, setMode] = useState(initial.mode);
  const [filterGroup, setFilterGroup] = useState(initial.group); // 'all' | one of GROUPS — scopes both the chart and the practice modes
  const [key, setKey] = useState(0); // force remount on script/mode change
  const [fullscreen, setFullscreen] = useState(initial.focus);
  const [allKana, setAllKana] = useState({ hiragana: HIRAGANA, katakana: KATAKANA });
  const [sessionData, setSessionData] = useState([]);
  const [progress, setProgress] = useState(EMPTY_KANA_PROGRESS);
  const [loadingSession, setLoadingSession] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [quizState, setQuizState] = useState(EMPTY_QUIZ_STATE);
  const [flashcardState, setFlashcardState] = useState(EMPTY_FLASHCARD_STATE);
  const [drawState, setDrawState] = useState(EMPTY_DRAW_STATE);   /* kana-draw 3/6 */
  const [writingState, setWritingState] = useState(EMPTY_WRITING_STATE);

  /* ── Path-launched stage ─────────────────────────────────────────
     `focus` is fixed for the life of the page: it describes how we were
     opened, so switching modes by hand later shouldn't retroactively turn
     a normal visit into a lesson (or vice versa). */
  const focus = initial.focus;
  const focusPool = useMemo(() => (focus ? initial.pool : []), [focus, initial.pool]);
  const node = focus && initial.lesson ? NODE_BY_ID[initial.lesson] : null;
  const [stageResult, setStageResult] = useState(null); // { passed, required, lessonDone }
  const [confirmQuit, setConfirmQuit] = useState(false);
  const reportedRef = useRef(false);


  const fallbackData = useMemo(() => (script === 'hiragana' ? HIRAGANA : KATAKANA), [script]);
  const data = allKana[script]?.length ? allKana[script] : fallbackData;
  const practiceData = useMemo(
    () => (mode === 'chart' ? data : (sessionData.length ? sessionData : data.slice(0, 25))),
    [mode, data, sessionData]
  );
  const activeMode = MODES.find(m => m.key === mode);
  const scriptLabel = script === 'hiragana' ? 'Hiragana' : 'Katakana';
  const activeKey = mode === 'chart'
    ? key
    : `${key}-${practiceData.map(item => item._id || item.kana).join('-')}`;

  const switchScript = (s) => { setScript(s); setKey(k => k + 1); };
  const switchMode = (m) => { setMode(m); setKey(k => k + 1); };

  useEffect(() => {
    let alive = true;
    setLoadError('');

    Promise.all([
      getKana({ type: script }),
      getKanaProgress(),
    ])
      .then(([kanaRes, progressRes]) => {
        if (!alive) return;
        setAllKana(prev => ({
          ...prev,
          [script]: Array.isArray(kanaRes.data) && kanaRes.data.length ? kanaRes.data : prev[script],
        }));
        setProgress(progressRes.data || EMPTY_KANA_PROGRESS);
      })
      .catch(err => {
        if (alive) setLoadError(err.message);
      });

    return () => { alive = false; };
  }, [script]);

  useEffect(() => {
    if (mode === 'chart') {
      setSessionData([]);
      setLoadingSession(false);
      setQuizState(EMPTY_QUIZ_STATE);
      setFlashcardState(EMPTY_FLASHCARD_STATE);
      setWritingState(EMPTY_WRITING_STATE);
      setDrawState(EMPTY_DRAW_STATE);   /* kana-draw 4/6 */
      return;
    }

    // Launched from the path: practise exactly the pool that stage asked
    // for. A review stage's pool is its own row plus every earlier row, and
    // we cap it so a late review doesn't turn into a 70-card slog — the
    // current row is always included, older rows are sampled.
    if (focusPool.length) {
      const own = new Set(node?.groups || []);
      const current = data.filter(item => own.has(item.group));
      const older = data.filter(item => focusPool.includes(item.group) && !own.has(item.group));
      const room = Math.max(0, REVIEW_SESSION_MAX - current.length);
      setSessionData(shuffle([...current, ...shuffle(older).slice(0, room)]));
      setLoadingSession(false);
      setLoadError('');
      return;
    }

    // A specific row/group is selected — scope the whole session to just
    // those characters instead of the backend's adaptive whole-script pick
    // (which samples ~14-25 items across all 71 and would rarely include
    // e.g. every K-row character together).
    if (filterGroup !== 'all') {
      const groupItems = data.filter(item => item.group === filterGroup);
      setSessionData(shuffle(groupItems));
      setLoadingSession(false);
      setLoadError('');
      return;
    }

    let alive = true;
    setLoadingSession(true);
    setLoadError('');

    getKanaSession({ type: script })
      .then(res => {
        if (!alive) return;
        setSessionData(Array.isArray(res.data) && res.data.length ? res.data : fallbackSession(fallbackData));
        if (res.progress) setProgress(res.progress);
      })
      .catch(err => {
        if (!alive) return;
        setLoadError(err.message);
        setSessionData(fallbackSession(fallbackData));
      })
      .finally(() => {
        if (alive) setLoadingSession(false);
      });

    return () => { alive = false; };
  }, [script, mode, key, fallbackData, filterGroup, data, focusPool, node]);

  useEffect(() => {
    if (mode !== 'quiz' || loadingSession || !practiceData.length) {
      if (mode !== 'quiz') setQuizState(EMPTY_QUIZ_STATE);
      return;
    }

    const cards = shuffle(practiceData);
    setQuizState({
      cards,
      options: cards.map(card => getOptions(card, data)),
      index: 0,
      selected: null,
      score: 0,
      done: false,
      reverse: false,
    });
  }, [mode, activeKey, loadingSession, practiceData, data]);

  useEffect(() => {
    if (mode !== 'flashcard' || loadingSession || !practiceData.length) {
      if (mode !== 'flashcard') setFlashcardState(EMPTY_FLASHCARD_STATE);
      return;
    }

    setFlashcardState({
      cards: shuffle(practiceData),
      index: 0,
      flipped: false,
      known: [],
      unknown: [],
      done: false,
    });
  }, [mode, activeKey, loadingSession, practiceData]);

  useEffect(() => {
    if (mode !== 'writing' || loadingSession || !practiceData.length) {
      if (mode !== 'writing') setWritingState(EMPTY_WRITING_STATE);
      return;
    }

    setWritingState({
      cards: shuffle(practiceData),
      index: 0,
      input: '',
      result: null,
      score: 0,
      done: false,
      showHint: false,
    });
  }, [mode, activeKey, loadingSession, practiceData]);

  /* kana-draw 5/6 */
  useEffect(() => {
    if (mode !== 'draw' || loadingSession || !practiceData.length) {
      if (mode !== 'draw') setDrawState(EMPTY_DRAW_STATE);
      return;
    }

    setDrawState({
      cards: shuffle(practiceData),
      index: 0,
      revealed: false,
      correct: 0,
      done: false,
    });
  }, [mode, activeKey, loadingSession, practiceData]);

  /* Finishing any practice session is the moment to celebrate — but only if
     this session is what earned the day. That logic now lives in one place
     and is shared with every other exercise. */
  const sessionJustFinished = quizState.done || flashcardState.done || writingState.done;
  const { celebration, dismiss: dismissCelebration } = useStreakCelebration(sessionJustFinished);

  /* What the learner actually scored, whichever exercise this was. */
  const sessionScore = useMemo(() => {
    if (flashcardState.done) {
      const total = flashcardState.known.length + flashcardState.unknown.length;
      return { correct: flashcardState.known.length, total, label: 'known' };
    }
    if (quizState.done) {
      return { correct: quizState.score, total: quizState.cards.length, label: 'correct' };
    }
    if (writingState.done) {
      return { correct: writingState.score, total: writingState.cards.length, label: 'correct' };
    }
    return null;
  }, [flashcardState, quizState, writingState]);

  const scorePct = sessionScore?.total
    ? Math.round((sessionScore.correct / sessionScore.total) * 100)
    : 0;

  /* Bank the stage. Guarded by a ref rather than state so React's double
     render in StrictMode can't post it twice. */
  useEffect(() => {
    if (!focus || !sessionJustFinished || reportedRef.current) return;
    if (!initial.lesson || initial.stage === null) return;
    reportedRef.current = true;

    completeRoadmapStage({ lessonId: initial.lesson, stage: initial.stage, scorePct })
      .then(res => setStageResult(res.data))
      .catch(() => setStageResult({ passed: false, failed: true }));
  }, [focus, sessionJustFinished, initial.lesson, initial.stage, scorePct]);

  const leaveToPath = useCallback(() => navigate('/dashboard'), [navigate]);

  /* Straight on to the next exercise of this row.

     A row is four stages, and the only way forward used to be "Back to
     path" followed by finding and tapping the node you were already
     standing on — four times per row. The server tells us which stage is
     next (it owns the plan), so the result screen can just offer it.

     Navigating changes only the query string, and `initial` is read once
     from window.location.search, so the route is keyed on the search in
     App.js to force a clean remount. Without that key this button would
     change the URL and replay the same stage. */
  const nextStage = stageResult?.nextStage;
  const nextPlan = stageResult?.nextStagePlan;
  /* And once the row itself is finished, the first stage of the next row.
     Without this the button vanished on the one screen where carrying on
     matters most — "K row complete!" offered nothing but Back to path. */
  const nextLesson = stageResult?.nextLesson;
  const nextLessonNode = nextLesson ? NODE_BY_ID[nextLesson.lessonId] : null;

  const goToNextStage = useCallback(() => {
    if (node && nextPlan && typeof nextStage === 'number' && nextStage >= 0) {
      navigate(stageHref(node, nextStage, nextPlan));
      return;
    }
    if (nextLessonNode && nextLesson) {
      navigate(stageHref(nextLessonNode, nextLesson.stage, nextLesson));
    }
  }, [navigate, node, nextStage, nextPlan, nextLessonNode, nextLesson]);

  const canGoNext = !!(stageResult?.passed
    && ((node && nextPlan) || (nextLessonNode && nextLesson)));

  /* Just "Continue", with a count of the step you are about to start.

     Naming the next exercise ("Recognise them", "Start S row") read as a
     different feature rather than the obvious way onward. A count says
     the one thing that matters — how far through the row you will be —
     and it counts the step ahead, not the one behind: finishing the
     first of four and being shown "1/4" describes where you have been,
     which is what the line above the button already says.

     Once the row is finished the count is dropped altogether. "4/4" on a
     button that leaves for a different row is worse than no number: it
     labels the destination with the progress of the place you are
     leaving. */
  const nextProgress = nextPlan && stageResult?.total
    ? `${nextStage + 1}/${stageResult.total}`
    : null;

  const nextButtonLabel = nextPlan
    ? 'Continue'
    : (nextLesson?.key === 'boss' ? 'Boss battle' : 'Next row');

  /* Bailing out mid-session throws away everything answered so far, so ask
     first. Nothing to lose before the first answer or after the result. */
  const closeFullscreen = useCallback(() => setFullscreen(false), []);
  const exitTarget = focus ? leaveToPath : closeFullscreen;
  const retryStage = useCallback(() => {
    reportedRef.current = false;
    setStageResult(null);
    setKey(k => k + 1);
  }, []);

  const handleGrade = useCallback((card, correct) => {
    if (!card?.kana) return;

    updateKanaProgress({ type: script, kana: card.kana, correct })
      .then(res => {
        if (res.data) setProgress(res.data);
      })
      .catch(() => {});
  }, [script]);

  useEffect(() => {
    if (!fullscreen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [fullscreen]);

  /* In focus mode the exercise owns the whole screen, so the three modes'
     own "Try Again / reload the page" end screens are wrong: reloading
     would replay the stage, and there is no page chrome to get back to the
     path with. Swap in one result screen that reports the stage and puts
     the way back to the path where the learner is already looking. */
  const started = quizState.index > 0 || writingState.index > 0
    || flashcardState.known.length > 0 || flashcardState.unknown.length > 0;

  /* The ✕ has always asked; the sidebar never did. Same condition, so both
     exits now behave the same way. */
  useStudyGuard(started && !sessionJustFinished);
  const requestExit = () => {
    if (sessionJustFinished || !started) exitTarget();
    else setConfirmQuit(true);
  };

  const lessonComplete = focus && sessionJustFinished && (
    <SessionComplete
      scorePct={scorePct}
      passed={stageResult?.passed}
      correct={sessionScore?.correct}
      total={sessionScore?.total}
      label={sessionScore?.label}
      title={
        stageResult?.passed === false
          ? 'Not quite yet'
          : stageResult?.lessonDone
            ? `${node?.title || 'Row'} complete!`
            : 'Step complete!'
      }
      detail={
        stageResult?.passed === false
          ? `Score ${stageResult.required}% or more to bank this step — have another go.`
          : stageResult?.lessonDone
            ? 'Every step of this row is done. The next row is unlocked.'
            : stageResult
              ? `${stageResult.stagesDone.length} of ${stageResult.total} steps banked.`
              : 'Saving your progress…'
      }
      onRetry={retryStage}
      onExit={leaveToPath}
      /* Only when this stage banked. Offering "next step" after a fail
         would walk the learner past the drill they just did not pass. */
      onNext={canGoNext ? goToNextStage : undefined}
      nextLabel={nextButtonLabel}
      nextProgress={nextProgress}
    />
  );

  const renderMode = (isFullscreen = false) => (
    <>
      {lessonComplete}
      {!lessonComplete && mode === 'chart' && (
        <ChartMode
          data={data}
          type={scriptLabel}
          script={script}
          progress={progress}
          filterGroup={filterGroup}
          onFilterGroupChange={setFilterGroup}
        />
      )}
      {!lessonComplete && mode === 'flashcard' && (
        <FlashcardMode
          script={script}
          progress={progress}
          onGrade={handleGrade}
          flashcardState={flashcardState}
          setFlashcardState={setFlashcardState}
          fullscreen={isFullscreen}
        />
      )}
      {!lessonComplete && mode === 'quiz' && (
        <QuizMode
          allData={data}
          script={script}
          progress={progress}
          onGrade={handleGrade}
          quizState={quizState}
          setQuizState={setQuizState}
          fullscreen={isFullscreen}
          keyboardEnabled={!fullscreen || isFullscreen}
        />
      )}
      {!lessonComplete && mode === 'writing' && (
        <WritingMode
          script={script}
          progress={progress}
          onGrade={handleGrade}
          writingState={writingState}
          setWritingState={setWritingState}
          fullscreen={isFullscreen}
          keyboardEnabled={!fullscreen || isFullscreen}
        />
      )}
      {/* kana-draw 6/6 */}
      {!lessonComplete && mode === 'draw' && (
        <DrawMode
          script={script}
          allData={data}
          onGrade={handleGrade}
          drawState={drawState}
          setDrawState={setDrawState}
          fullscreen={isFullscreen}
          keyboardEnabled={!fullscreen || isFullscreen}
        />
      )}
    </>
  );

  return (
    <div style={{ padding: 32 }} className="anim-fade-up">

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        {/* The count belongs with the title, not down in the picker row —
            it describes the script you are looking at, and beside the
            heading it reads as part of the sentence rather than as a third
            control sitting among two real ones. */}
        <h1 style={{ fontSize: 22, fontWeight: 900, letterSpacing: -0.5, marginBottom: 6,
                     display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          Hiragana &amp; Katakana
          <Tag color={C.accent}>{data.length} characters</Tag>
        </h1>
        <p style={{ color: C.muted, fontSize: 14 }}>
          Master the Japanese alphabets with 4 different study modes.
        </p>
      </div>

      {/* Script picker and mode tabs share one row.

          They were stacked, which left a wide empty band to the right of
          the script cards and pushed the chart itself further down the
          page than it needed to be. Side by side, the two things you
          choose before studying sit together and the chart comes up
          sooner. The script cards are smaller than they were for the same
          reason — at 140px wide with 12/20 padding they were the largest
          thing on a page whose subject is the grid below them. */}
      <div className="kana-head-row">
        <div className="kana-script-toggle">
          {[
            { key: 'hiragana', label: 'Hiragana', sub: 'あいうえお' },
            { key: 'katakana', label: 'Katakana', sub: 'アイウエオ' },
          ].map(s => (
            <div
              key={s.key}
              onClick={() => switchScript(s.key)}
              className={`kana-script-card${script === s.key ? ' active' : ''}`}
            >
              <div className="kana-script-name">{s.label}</div>
              <div className="kana-script-sub">{s.sub}</div>
            </div>
          ))}

        </div>

        <div className="kana-mode-toolbar">
          <div className="kana-mode-strip">
            {MODES.map(m => (
              <ModeTab key={m.key} icon={m.icon} label={m.label} active={mode === m.key} onClick={() => switchMode(m.key)} />
            ))}
          </div>
          <button
            className="btn-ghost kana-fullscreen-btn"
            onClick={() => setFullscreen(true)}
            title={`Open ${activeMode?.label || 'mode'} full screen`}
          >
            <RiFullscreenLine size={18} />
          </button>
        </div>
      </div>

      {/* Practice scope — the row/group filter picked on the Chart tab keeps
          scoping Flashcards/Quiz/Writing too, so surface it everywhere,
          not just where it was set. */}
      {filterGroup !== 'all' && (
        <div className="kana-scope-banner" style={{ marginBottom: 18 }}>
          <RiFilterLine size={14} />
          <span>Practicing <strong>{GROUP_LABELS[filterGroup] || filterGroup}</strong> only</span>
          <button type="button" onClick={() => setFilterGroup('all')} title="Practice all characters">
            <RiCloseLine size={14} /> Clear
          </button>
        </div>
      )}

      {/* Active mode */}
      {loadError && (
        <div style={{ color: C.red, fontSize: 12, fontWeight: 800, marginBottom: 14 }}>
          {loadError}
        </div>
      )}
      <div key={activeKey}>
        {loadingSession && mode !== 'chart' ? (
          <div className="ks-card" style={{ padding: 32, textAlign: 'center', color: C.muted, fontWeight: 800 }}>
            Preparing your adaptive {activeMode?.label?.toLowerCase()} session...
          </div>
        ) : renderMode()}
      </div>

      {fullscreen && createPortal(
        <div
          className="study-fullscreen-shell kana-fullscreen-shell"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1200,
            background: C.bg,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
          }}
        >
          <div
            className="study-fullscreen-header kana-fullscreen-header"
            style={{
            height: 64,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 26px',
            borderBottom: `1px solid ${C.border}`,
            background: 'rgba(8,8,14,0.94)',
            backdropFilter: 'blur(18px)',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
          >
            <div>
              <div style={{ fontSize: 15, fontWeight: 900 }}>
                {focus && node ? node.title : activeMode?.label}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
                <Tag color={C.accent}>{scriptLabel}</Tag>
                {focus ? (
                  <>
                    {initial.review && <Tag color={C.purple}>Mixed review</Tag>}
                    <span style={{ color: C.muted, fontSize: 12 }}>
                      {activeMode?.label} · {practiceData.length} characters
                    </span>
                  </>
                ) : (
                  <>
                    {filterGroup !== 'all' && <Tag color={C.yellow}>{GROUP_LABELS[filterGroup] || filterGroup}</Tag>}
                    <span style={{ color: C.muted, fontSize: 12 }}>{data.length} characters</span>
                  </>
                )}
              </div>
            </div>
            {/* Launched from the path, so closing goes back to the path —
                not to the kana page the learner never chose to be on. */}
            <button
              className="btn-icon study-fullscreen-close kana-fullscreen-close"
              onClick={requestExit}
              title={focus ? 'Back to your path' : 'Close full screen'}
            >
              <RiCloseLine size={18} />
            </button>
          </div>

          <div
            key={`fullscreen-${activeKey}-${script}-${mode}`}
            style={{
              flex: 1,
              width: '100%',
              maxWidth: mode === 'chart' ? 1180 : 880,
              margin: '0 auto',
              padding: mode === 'chart' ? '32px 24px 60px' : '24px 24px 48px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: mode === 'chart' ? 'flex-start' : 'center',
            }}
          >
            {loadingSession && mode !== 'chart' ? (
              <div className="ks-card" style={{ padding: 32, textAlign: 'center', color: C.muted, fontWeight: 800 }}>
                Preparing your adaptive {activeMode?.label?.toLowerCase()} session...
              </div>
            ) : renderMode(true)}
          </div>
        </div>,
        document.body
      )}

      <QuitConfirm
        open={confirmQuit}
        message="You'll lose the answers from this session."
        onConfirm={() => { setConfirmQuit(false); exitTarget(); }}
        onCancel={() => setConfirmQuit(false)}
      />

      {celebration && (
        <StreakCelebration
          streak={celebration.streak}
          lastStudied={celebration.lastStudied}
          onContinue={dismissCelebration}
        />
      )}
    </div>
  );
}
